import { useNavigate, Navigate, useParams } from 'react-router-dom';
import React, { useState, useEffect, useContext } from 'react';
import * as api from "../../api/apiConnector.js";
import UserContext from '../../components/User_Context.js';
import * as menu from "../../assets/menus/menu.js"
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2'
import * as utils from "../../components/utils.js";
import waiting from "../../assets/images/waiting.svg";
import { dms_style } from "../../styles/style.js";
import Undo_Redo from "../../components/ui/undo_redo.js";
import Choices from '../../components/Choices.js';
import { useTranslation } from 'react-i18next';
import { systemLabels } from '../label/system_labels.js';

const Organizational_Chart = (params) => {
    // Route parameter - organizational unit ID
    const { id } = useParams();
    
    // State management for organizational chart data
    const [unit, setUnit] = useState(null);                    // Current organizational unit
    const [chart, setChart] = useState(null);                  // User-role connections array
    const { currentUser } = useContext(UserContext);           // Current authenticated user
    const [currentLink, setCurrentLink] = useState(null);      // Link being edited/created
    const [searchContent, setSearchContent] = useState(null);  // User search results
    const [roles, setRoles] = useState(null);                  // Available organizational roles
    const { t } = useTranslation();                            // Translation function
    let undo_redo = null;                                      // Undo/Redo functionality reference

    // Initialize internationalization labels from centralized file
    const i18n = menu.getI18n();
    i18n.addResourceBundle('it', 'labels', systemLabels.it, true, true);
    i18n.addResourceBundle('en', 'labels', systemLabels.en, true, true);

    /**
     * Normalize date fields in chart data
     * Converts ISO date strings to Date objects for proper handling in form inputs
     * @param {Array} data - Array of user-role connections
     * @returns {Array} Normalized data with Date objects
     */
    const _normData = (data) => {
        for (const c of data) {
            c.RoleFrom = c.RoleFrom ? new Date(c.RoleFrom) : null;
            c.RoleTo = c.RoleTo ? new Date(c.RoleTo) : null;
        }

        return data;
    }

    /**
     * Fetch organizational unit data and user-role connections
     * Retrieves the business unit, available roles, and existing user connections
     * @param {Object} params - Optional parameters (currently unused)
     */
    const _getData = async (params) => {
        const units = (await api.sendMessage("admin/system/organization/get_business_units", {
            user: currentUser(),
            body: {
                ids: [id]
            }
        }))?.units ?? [];

        const roles = await api.sendMessage("admin/vocs/get", {
            user: currentUser(),
            body: {
                voc_id: "organizational_chart_roles"
            }
        });

        setRoles(roles.items);

        if (units.length > 0) {
            setUnit(units[0]);

            // Usa il nuovo endpoint per recuperare le connessioni utente-ruolo
            const chart = await api.sendMessage("admin/system/organization/get_user_connections_by_parent", {
                user: currentUser(),
                body: { parentId: id }
            });

            if (chart.success) {

                
                setChart(utils.clone(_normData(chart.items)));
                undo_redo.push(chart.items);
            } else {
                withReactContent(Swal).fire({
                    title: t('error', {ns:'labels'}),
                    icon: "error",
                    text: chart.error || chart.message,
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    showCloseButton: false,
                })
            }
        }
        else {
            withReactContent(Swal).fire({
                title: t('error', {ns:'labels'}),
                icon: "error",
                text: t('businessUnitNotFound', {ns:'labels'}),
                showConfirmButton: true,
                allowOutsideClick: false,
                showCloseButton: false,
                confirmButtonText: t('goToBusinessUnit', {ns:'labels'})
            }).then((r) => {
                if (r.isConfirmed) {
                    window.location.href = '/admin/organization/business_units';
                }
            });
        }
    }

    /**
     * Update chart state and push to undo/redo stack
     * Normalizes data and triggers re-render of the chart
     */
    const _update = () => {
        undo_redo.push(chart);
        setChart(utils.clone(_normData(chart)));
    }

    //#region User Search Functionality
    let _searchChangeTimer = null;

    /**
     * Handle search input changes with debouncing
     * Waits 250ms after user stops typing before executing search
     * @param {Event} e - Input change event
     */
    const _searchChange = async (e) => {
        let _text = e.target.value;

        if (_searchChangeTimer) {
            clearTimeout(_searchChangeTimer);
        }

        _searchChangeTimer = setTimeout(async () => {
            if (_text) {
                let search_result = await search(_text);
                setSearchContent(search_result);
            }
        }, 250);
    }

    /**
     * Search for users by name
     * @param {string} name - Search term (user name)
     * @returns {Array} Array of matching users
     */
    const search = async (name) => {
        if (!name) return;
        const result = await api.sendMessage("usersGroups/search", {
            user: currentUser(),
            body: {
                name,
                searchGroups: false
            }
        });

        return result.filtered.users;
    }

    /**
     * Render search results table
     * Displays found users in a table format with name and email
     * @returns {JSX.Element} Search table component
     */
    const _drawSearchTable = () => {
        return (
            <div className="tab-content">
                <table className="table">
                    <thead>
                        <tr>
                            <th>{t('name', {ns:'labels'})}</th>
                            <th>{t('email', {ns:'labels'})}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {searchContent ? (_drawSearchTableRows()) : (<div className="text-center"><img src={waiting} /></div>)}
                    </tbody>
                </table>
            </div>);
    }

    /**
     * Render all search result rows
     * @returns {Array<JSX.Element>} Array of table row components
     */
    const _drawSearchTableRows = () => {
        return searchContent.map((i) => { return (_drawSearchTableRow(i)) });
    }

    /**
     * Render individual search result row
     * Double-click to select user for current connection
     * @param {Object} item - User object from search results
     * @returns {JSX.Element} Table row component
     */
    const _drawSearchTableRow = (item) => {
        return (<tr data-id={item.Id} style={{ ...dms_style.table_row }}
            onDoubleClick={(e) => {
                currentLink.UserId = item.Id;
                currentLink.User_Name = item.Name;
                currentLink.User_LastName = item.LastName;
                currentLink.__toUpdate = true;
                chart.push(currentLink);
                setCurrentLink(null);
                _update();
            }}
        >
            <td>
                <p><strong>{item.LastName}</strong> {item.Name} </p>
            </td>
            <td>
                <p>{item.EMail}</p>
            </td>
        </tr>);
    }

    //#endregion

    const userFindModal = () => {
        const _validate = () => {
            // if (!currentLink.Name) return false;
            // if (!currentLink.UnitTypeCode) return false;
            return true;
        }

        const _confirmEdit = () => {
            currentLink.__toUpdate = true;
            chart.push(currentLink);
            setCurrentLink(null);
            _update();
        }


        if (currentLink) {
            return (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div id="business_unit_edit_modal" className="modal fade show" style={{ display: "block" }} >
                        <div className="modal-dialog modal-xl  modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">{t('chooseUser', {ns:'labels'})}</h5>
                                    <button type="button" className="btn-close" aria-label="Close"
                                        onClick={(e) => {
                                            setCurrentLink(null);
                                        }}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <div>
                                        <label for="business_unit_name" className="form-label">{t('user', {ns:'labels'})}<span className="text-danger">*</span></label>
                                        <input type="search" className="form-control input-rounded" placeholder={t('search', {ns:'labels'})}
                                            onChange={_searchChange}
                                        />

                                        {searchContent ? _drawSearchTable() : (null)}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary"
                                        onClick={(e) => {
                                            setCurrentLink(null);
                                        }}
                                    >{t('close', {ns:'labels'})}</button>
                                    <button type="button" onClick={(e) => {
                                        _confirmEdit();
                                    }}
                                        className="btn btn-primary" disabled={_validate ? null : true}>{t('ok', {ns:'labels'})}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )
        }
        else {
            return (null);
        }

    }


    useEffect(() => {
        (async () => {
            // setBreadCrumb();
            menu.write({ user: currentUser() });
            await _getData();
        })();
    }, []);

    useEffect(() => {

    }, [searchContent])



    useEffect(() => {
        _table()
    }, [chart]);

    const _add_user_link = async () => {
        const _link = await _getNewUserLink();
        if (_link) {
            _link.ParentId = unit.Id; // Usa ParentId invece di UnitId
            _link.__toUpdate = true;
            _link.RoleFrom = new Date();
            setCurrentLink(utils.clone(_link));
        }
    }

    const _getNewUserLink = async () => {
        const result = await api.sendMessage("admin/system/organization/get_new_user_connection", {
            user: currentUser()
        });

        if (result.success) {
            return result.link;
        } else {
            withReactContent(Swal).fire({
                title: t('error', {ns:'labels'}),
                icon: "error",
                text: result.error || t('errorGeneratingNewConnection', {ns:'labels'}),
                showConfirmButton: true
            });
            return null;
        }
    }

    /**
     * Validate user-role connections before saving
     * Checks for required fields and valid date ranges
     * @returns {Array<string>} Array of validation error messages
     */
    const _validate = () => {
        const errors = [];
        
        for (const connection of chart) {
            // Only validate connections marked for update and not marked for deletion
            if (connection.__toUpdate && !connection.__toDelete) {
                if (!connection.UserId) {
                    errors.push(t('missingUserForConnection', { ns: 'labels' }));
                }
                
                if (!connection.RoleCode) {
                    errors.push(t('missingRoleForConnection', { ns: 'labels' }));
                }
                
                if (!connection.ParentId) {
                    errors.push(t('missingParentIdForConnection', { ns: 'labels' }));
                }
                
                // Validate that RoleTo is after RoleFrom if both are present
                if (connection.RoleFrom && connection.RoleTo && connection.RoleTo <= connection.RoleFrom) {
                    errors.push(t('invalidDateRange', { ns: 'labels' }));
                }
            }
        }
        
        return errors;
    }

    /**
     * Get connections that need to be saved (created/updated/deleted)
     * @returns {Array} Connections marked for update or deletion
     */
    const _toSave = () => {
        return chart.filter(x => x.__toUpdate || x.__toDelete);
    }

    /**
     * Get connections marked for deletion
     * @returns {Array} Connections to be deleted
     */
    const _toDelete = () => {
        return chart.filter(x => x.__toDelete);
    }

    /**
     * Save all changes to user-role connections
     * Processes deletions first, then creates/updates
     * Provides detailed feedback on operation results
     * @returns {Object} Success status and any error information
     */
    const _save = async () => {
        const errors = _validate();

        withReactContent(Swal).fire({
            title: t('saving', { ns: 'labels' }),
            icon: "info",
            text: t('savingNewData', { ns: 'labels' }),
            allowOutsideClick: false,
            showConfirmButton: false
        });

        if (errors.length > 0) {
            withReactContent(Swal).fire({
                title: t('error', { ns: 'labels' }),
                icon: "error",
                text: errors.join(', '),
                showConfirmButton: true
            });
            return { success: false };
        }

        const dataToUpdate = chart.filter(x => x.__toUpdate && !x.__toDelete);
        const dataToDelete = _toDelete();

        if (dataToUpdate.length === 0 && dataToDelete.length === 0) {
            withReactContent(Swal).fire({
                title: t('nothingToSave', { ns: 'labels' }),
                icon: "info",
                text: t('nothingToSaveMessage', { ns: 'labels' }),
                showConfirmButton: true
            });
            return { success: false };
        }

        let saveCount = 0;
        let deleteCount = 0;
        let errorMessages = [];

        // Process deletions first to avoid conflicts
        for (const connection of dataToDelete) {
            try {
                const result = await api.sendMessage("admin/system/organization/delete_user_connection", {
                    user: currentUser(),
                    body: { id: connection.Id }
                });

                if (result.success) {
                    deleteCount++;
                } else {
                    errorMessages.push(result.error || t('deleteErrorForConnection', { ns: 'labels' }));
                }
            } catch (error) {
                errorMessages.push(t('deleteErrorForConnection', { ns: 'labels' }) + ': ' + error.message);
            }
        }

        // Process updates/creations after deletions are complete
        for (const connection of dataToUpdate) {
            try {
                const linkData = {
                    Id: connection.Id,
                    ParentId: connection.ParentId,
                    UserId: connection.UserId,
                    RoleCode: connection.RoleCode,
                    RoleFrom: connection.RoleFrom ? connection.RoleFrom.toISOString() : null,
                    RoleTo: connection.RoleTo ? connection.RoleTo.toISOString() : null
                };

                const result = await api.sendMessage("admin/system/organization/save_user_connection", {
                    user: currentUser(),
                    body: {
                        link: linkData
                    }
                });

                if (result.success) {
                    connection.__toUpdate = false;
                    saveCount++;
                } else {
                    errorMessages.push(result.error || t('saveErrorForConnection', { ns: 'labels' }));
                }
            } catch (error) {
                errorMessages.push(t('saveErrorForConnection', { ns: 'labels' }) + ': ' + error.message);
            }
        }

        // Rimuovi effettivamente gli elementi cancellati dal chart locale
        if (deleteCount > 0) {
            const updatedChart = chart.filter(c => !c.__toDelete);
            setChart(updatedChart);
            undo_redo.push(updatedChart);
        } else {
            // Aggiorna lo stato per riflettere le modifiche
            setChart(utils.clone(chart));
        }

        if (errorMessages.length > 0) {
            withReactContent(Swal).fire({
                title: t('partialSaveError', { ns: 'labels' }),
                icon: "warning",
                text: t('savedConnections', { ns: 'labels', count: saveCount }) + '. ' + 
                      t('deletedConnections', { ns: 'labels', count: deleteCount }) + '. ' +
                      t('errors', { ns: 'labels' }) + ': ' + errorMessages.join(', '),
                showConfirmButton: true
            });
        } else {
            const totalOperations = saveCount + deleteCount;
            let successMessage = '';
            
            if (saveCount > 0 && deleteCount > 0) {
                successMessage = t('savedAndDeletedConnections', { ns: 'labels', saved: saveCount, deleted: deleteCount });
            } else if (saveCount > 0) {
                successMessage = t('savedSussessfullyWithNumberMessage', { ns: 'labels', number: saveCount });
            } else if (deleteCount > 0) {
                successMessage = t('deletedSussessfullyWithNumberMessage', { ns: 'labels', number: deleteCount });
            }

            withReactContent(Swal).fire({
                title: t('success', { ns: 'labels' }),
                icon: "success",
                text: successMessage,
                showConfirmButton: true
            });
        }
        
        return { success: errorMessages.length === 0 };
    }

    /**
     * Mark a connection for deletion with user confirmation
     * Shows confirmation dialog before marking connection for deletion
     * @param {Object} connection - User-role connection to delete
     */
    const _markForDelete = (connection) => {
        withReactContent(Swal).fire({
            title: t('confirmDelete', { ns: 'labels' }),
            icon: "question",
            text: t('confirmDeleteConnection', { ns: 'labels' }),
            showConfirmButton: true,
            showDenyButton: true,
            showCloseButton: false,
            confirmButtonText: t('yes', { ns: 'labels' }),
            denyButtonText: t('no', { ns: 'labels' })
        }).then((result) => {
            if (result.isConfirmed) {
                connection.__toDelete = true;
                _update();
            }
        });
    }

    /**
     * Render a single table row for user-role connection
     * Handles editing, deletion marking, and undo functionality
     * @param {Object} row - User-role connection object
     * @returns {JSX.Element} Table row component
     */
    const _drawRow = (row) => {
        const isMarkedForDelete = row.__toDelete;
        const rowClass = isMarkedForDelete ? "table-danger text-decoration-line-through" : "";
        
        return (
            <tr className={rowClass}>
                <th scope="row">
                    {chart.findIndex(x => x === row) + 1}
                </th>
                <td>
                    {row.User_LastName} {row.User_Name}
                </td>
                <td>
                    <Choices voc={roles}                        
                        value={"Code"} text={"Name"}
                        selected={roles.find(x => x.Code == row.RoleCode) ? [roles.find(x => x.Code == row.RoleCode)] : []}
                        disabled={isMarkedForDelete}
                        onChange={(e) => {
                            if (!isMarkedForDelete) {
                                row.RoleCode = e.selected &&
                                    e.selected.length > 0 ? e.selected[0].Code : null;
                                row.__toUpdate = true;
                                _update();
                            }
                        }
                        }></Choices>
                </td>
                <td>
                    <input 
                        type="date" 
                        className="form-control" 
                        disabled={isMarkedForDelete}
                        ref={(r) => {
                            if (!r) return;
                            if (row.RoleFrom) {
                                r.valueAsDate = row.RoleFrom;
                            }
                        }} 
                        onChange={(e) => {
                            if (!isMarkedForDelete) {
                                row.RoleFrom = e.target.valueAsDate;
                                row.__toUpdate = true;
                            }
                        }}>
                    </input>
                </td>
                <td>
                    <input 
                        type="date" 
                        className="form-control" 
                        disabled={isMarkedForDelete}
                        min={row.RoleFrom ? row.RoleFrom.toISOString().split('T')[0] : ''}
                        ref={(r) => {
                            if (!r) return;
                            if (row.RoleTo) {
                                r.valueAsDate = row.RoleTo;
                            }
                        }} 
                        onChange={(e) => {
                            if (!isMarkedForDelete) {
                                row.RoleTo = e.target.valueAsDate;
                                row.__toUpdate = true;
                            }
                        }}>
                    </input>
                </td>
                <td>
                    {isMarkedForDelete ? (
                        <button 
                            type="button" 
                            className="btn btn-sm btn-outline-success"
                            onClick={() => {
                                row.__toDelete = false;
                                _update();
                            }}
                            title={t('undoDelete', { ns: 'labels' })}
                        >
                            <i className="fa-solid fa-undo"></i>
                        </button>
                    ) : (
                        <button 
                            type="button" 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => _markForDelete(row)}
                            title={t('deleteConnection', { ns: 'labels' })}
                        >
                            <i className="fa-solid fa-trash"></i>
                        </button>
                    )}
                </td>
            </tr>
        )
    }

    /**
     * Render all table rows for user-role connections
     * @returns {Array<JSX.Element>|undefined} Array of table row components
     */
    const _drawRows = () => {
        if (chart && chart.length > 0) {
            return (chart.map(x => _drawRow(x)));
        }

    }

    /**
     * Render the main organizational chart table
     * Displays all user-role connections with editing capabilities
     * @returns {JSX.Element|null} Table component or null if no data
     */
    const _table = () => {
        if (!chart) return (null);

        _normData(chart);

        return (<table className="table">
            <thead>
                <tr>
                    <th className="col-md-1" scope="col">#</th>
                    <th className="col-md-5">{t('name', {ns:'labels'})}</th>
                    <th className="col-md-5">{t('role', {ns:'labels'})}</th>
                    <th className="col-md-5">{t('from', {ns:'labels'})}</th>
                    <th className="col-md-5">{t('to', {ns:'labels'})}</th>
                    <th className="col-md-1">{t('actions', {ns:'labels'})}</th>
                </tr>
            </thead>
            <tbody>
                {_drawRows()}
            </tbody>
        </table>);
    }

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                </ol>
                <div>
                    <Undo_Redo onInit={(r) => {
                        undo_redo = r;
                    }}
                        onChange={(d) => {
                            setChart(d);
                        }}
                    ></Undo_Redo>
                    <a className="text-primary me-2 fs-13" onClick={async (e) => {
                        _add_user_link();
                    }} role="button" title="Join a meet"><i className="fa-solid fa-plus fa-2x"></i>
                    </a>
                    <a className="text-primary me-2 fs-13" onClick={(e) => {
                        _save();
                    }} role="button" aria-controls="offcanvasExample1" title="Save this data"><i className="fa-solid fa-floppy-disk fa-2x"></i></a>
                </div>
            </div>

            <div style={{
                width: "100%",
                overflow: "scroll"

            }}>
            </div>

            <div className="container-fluid">
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-body p-0">
                                <div className="tab-content" id="tabs-content" style={{ padding: 15 }}>
                                    {chart ? _table() : (null)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {userFindModal()}
        </>

    )


}

export { Organizational_Chart }